// Commercial demos of the prebuilt formats (gallery of «Crear creativo»). A demo SHOWS what a format can do with a
// finished, fictional brand; the editable template (prebuilt.ts) is what the person really creates, with THEIR
// texts, colors and files. The two never mix: using a format never copies anything of the demo's brand.
//
// The documents are built by demos/build.ts with the production compiler (verbatim from the commercial website for
// Fizzora, Nubli, Zancada, Rumbora and Auralo) and run here with the served Creative Runtime (demo-player.tsx).

/** What demos/build.ts writes for each demo (src/features/create/demos/gen/<id>.json). */
export interface BuiltDemo {
  readonly id: string;
  readonly title: string;
  readonly artifactHash: string;
  readonly variants: readonly DemoVariant[];
  readonly assetUrls: Readonly<Record<string, string>>;
  readonly fonts: Readonly<Record<string, string>>;
  readonly links: Readonly<Record<string, string>>;
}
export interface DemoVariant {
  readonly layoutId: string;
  readonly width: number;
  readonly height: number;
  readonly formatKeys: readonly string[];
  readonly document: unknown;
}

export interface FormatDemo {
  /** File of the demo (gen/<demo>.json). */
  readonly demo: string;
  /** Fictional brand of the demo (never the customer's). */
  readonly brand: string;
  /** One line on what the demo shows. */
  readonly pitch: string;
  /** Layout shown on the gallery card (and first in its demo). */
  readonly cardLayout: string;
}

/** Prebuilt format id (experiences.ts) → its commercial demo. */
export const FORMAT_DEMOS: Readonly<Record<string, FormatDemo>> = {
  banner: {
    demo: "fizzora",
    brand: "Fizzora",
    pitch: "Lanzamiento de una bebida: entrada animada, selector de sabor, beneficios y cierre.",
    cardLayout: "mrec",
  },
  carousel: {
    demo: "fizzora",
    brand: "Fizzora",
    pitch: "Elige un sabor y recorre los beneficios del producto en un carrusel.",
    cardLayout: "inter",
  },
  "video-banner": {
    demo: "auralo",
    brand: "Auralo",
    pitch: "Video con puntos interactivos, selector de color y un final que la persona elige.",
    cardLayout: "mrec",
  },
  survey: {
    demo: "rumbora",
    brand: "Rumbora",
    pitch: "Cada respuesta cambia la imagen y la siguiente pregunta; al final, el destino ideal.",
    cardLayout: "mrec",
  },
  "game-find": {
    demo: "find",
    brand: "Molienda Sol",
    pitch: "Una cafetería ilustrada con cinco diferencias, vidas, tiempo y premio al final.",
    cardLayout: "mrec",
  },
  "game-pairs": {
    demo: "nubli",
    brand: "Nubli",
    pitch: "Seis parejas de helados contra el reloj, con puntaje y volver a jugar.",
    cardLayout: "mrec",
  },
  "game-tap": {
    demo: "catch",
    brand: "Fruvi",
    pitch: "Atrapa la fruta antes de que salte y esquiva a la abeja: 10 puntos para ganar.",
    cardLayout: "mrec",
  },
  "game-quiz": {
    demo: "quiz",
    brand: "Kairo",
    pitch: "Tres preguntas sobre hábitos saludables con el reloj de la marca como premio.",
    cardLayout: "mrec",
  },
  "game-drag": {
    demo: "drag",
    brand: "Ecora",
    pitch: "Lleva cada envase a su contenedor: reciclaje con la marca de limpieza.",
    cardLayout: "mrec",
  },
  "game-runner": {
    demo: "zancada",
    brand: "Zancada",
    pitch: "Corre por la ciudad, el parque y la playa: salta, agáchate y junta energía.",
    cardLayout: "mrec",
  },
};

const FILES = import.meta.glob<{ default: BuiltDemo }>("./gen/*.json");
const loads = new Map<string, Promise<BuiltDemo | null>>();

/** Loads a demo once per page (each demo is its own lazily loaded chunk). */
export function loadDemo(id: string): Promise<BuiltDemo | null> {
  let p = loads.get(id);
  if (!p) {
    const load = FILES[`./gen/${id}.json`];
    p = load ? load().then((m) => m.default) : Promise.resolve(null);
    loads.set(id, p);
  }
  return p;
}

export function variantOf(demo: BuiltDemo, layoutId: string): DemoVariant | undefined {
  return demo.variants.find((v) => v.layoutId === layoutId) ?? demo.variants[0];
}

/** Human size of a layout, e.g. «300 × 600». */
export const layoutLabel = (v: Pick<DemoVariant, "width" | "height">) => `${String(v.width)} × ${String(v.height)}`;
